
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import TradingBotStats from "@/models/TradingBotStats";
import TradingBotChip from "./TradingBotChip.vue";
import SearchBar from "./TradingBotSearchBar.vue";
import SubscriptionPlan from "@/models/subscription/SubscriptionPlan";
import cloneDeep from "lodash/cloneDeep";

@Component({
  components: {
    TradingBotChip,
    SearchBar,
    UpgradeSubscriptionPlanView: () =>
      import("./UpgradeSubscriptionPlanView.vue"),
    ConfirmationDialog: () => import("@/components/ConfirmationDialog.vue"),
    BotsOBTFuelEstimation: () => import("./BotsOBTFuelEstimation.vue"),
    ArchiveBotsDialog: () => import("./ArchiveBotsDialog.vue"),
    StopLossConfigurationDialog: () =>
      import("./StopLossConfigurationDialog.vue"),
  },
})
export default class TradingBotsWrapper extends Vue {
  // 📃 Possible bot sections
  private SECTIONS = {
    ACTIVE_BOTS: 1,
    ARCHIVED_BOTS: 2,
  };

  // 📃 The possible state components that could be shown on screen
  private POSSIBLE_COMPONENTS = {
    LOADING: 0,
    PLACEHOLDER: 1,
    BOTS: 2,
  };

  // 📦 Selected trading bot
  public selectedTrader!: TradingBotStats | null;

  // 📦 Mode responsible for fetching different types of trading bots (Active / Archived)
  private mode: number = 0;

  // 🚩 Flag for whether data is being fetched from the API
  @Prop({ default: false })
  private isLoading!: boolean;

  // 📦 Bot stats for all fetched trading bots
  public tradingBotStats: TradingBotStats[] = [];

  // 📦 Bot stats that are filtered by the provided search
  private filteredTradingBotsStats: TradingBotStats[] = [];

  // 🚩 Object which holds whether the bot lists for each section should be expanded
  private viewAll: any = {
    active: false,
    archived: false,
  };

  // 🚩 Flag for whether the delete bot icons should be shown
  private showDelete: boolean = false;

  // 🚩 Flag for whether the delete archived bot dialog should be shown
  private showDeleteArchivedBotDialog: boolean = false;

  // 🚩 Flag for whether the bulk archive bots dialog should be shown
  private showArchiveBotsDialog: boolean = false;

  // 🚩 Flag for whether the dialog for configuring stop-loss for bots should be shown
  private showConfigureStopLossDialog: boolean = false;

  // 📦 The archived bot which is selected for deletion
  private archivedBotToDelete: TradingBotStats | null = null;

  // 📦 Dynamic number of bots that are visible for the section
  private numberOfShownBots: number = this
    .defaultNumberOfVisibleBotsForScreenSize;

  private displayTradingBotStats: TradingBotStats[] = [];

  // 📦 Title for the button which responsible for expanding / collapsing the bot list section
  private expansionButtonText: string = "View All";

  // 🗻 The default number of bots that will be visible depending on the screen size
  private get defaultNumberOfVisibleBotsForScreenSize(): number {
    return this.$vuetify.breakpoint.mobile ? 3 : 12;
  }

  // 📦 The total number of active bots of the current user
  private get totalActiveBots(): number {
    return this.$store.getters.totalActiveBots;
  }

  // 📦 The total number of archived bots
  private get totalArchivedBots(): number {
    return this.$store.getters.totalArchivedBots;
  }

  // 📦 The current subscription plan of the user
  private get currentSubscriptionPlan(): SubscriptionPlan {
    return this.$store.state.subscription.currentSubscriptionPlan;
  }

  // 🚩 Flag for whether the user has paid/will pay for their current plan
  private get isPaidSubscription(): boolean {
    return this.$store.getters.isSubscriptionPaid;
  }

  // 📦 The state component that is currently shown to the user
  private get currentlyVisibleComponents(): number {
    if (this.isLoading && this.displayTradingBotStats.length == 0) {
      return this.POSSIBLE_COMPONENTS.LOADING;
    }
    if (this.tradingBotStats.length > 0) {
      return this.POSSIBLE_COMPONENTS.BOTS;
    }
    return this.POSSIBLE_COMPONENTS.PLACEHOLDER;
  }

  @Watch("filteredTradingBotsStats")
  private onChangeInTheFilteredTradingBotStats(
    val: TradingBotStats[],
    oldVal: TradingBotStats[]
  ) {
    this.updateExpandOrCollapseProperties(
      this.mode == 0 ? this.viewAll.active : this.viewAll.archived
    );
  }

  /**
   * 👂 Listener which is invoked when a mode is switched (Active / Archived)
   */
  @Watch("mode")
  private onModeSwitch(val: number, oldVal?: number) {
    this.selectedTrader = null;

    // Check whether it is in Active or Archived section and update the bots
    if (val == 0) {
      this.tradingBotStats = this.$store.getters.activeBots;
      this.updateExpandOrCollapseProperties(this.viewAll.active);
    } else {
      this.tradingBotStats = this.$store.getters.archivedBots;
      this.updateExpandOrCollapseProperties(this.viewAll.archived);
    }
  }

  private async mounted() {
    this.tradingBotStats = this.$store.getters.activeBots;

    this.$store.subscribe((mutation, state) => {
      if (mutation.type === "setAllBots") {
        this.tradingBotStats =
          this.mode === 0
            ? this.$store.getters.activeBots
            : this.$store.getters.archivedBots;
      }
    });
  }

  /**
   * 🏗 Expands or collapses the bots list section
   */
  private expandOrCollapseBotsList() {
    switch (this.mode) {
      case 0: {
        this.viewAll.active = !this.viewAll.active;
        this.updateExpandOrCollapseProperties(this.viewAll.active);
        break;
      }
      case 1: {
        this.viewAll.archived = !this.viewAll.archived;
        this.updateExpandOrCollapseProperties(this.viewAll.archived);
        break;
      }
    }
  }

  /**
   * 🏗 Updates the number of bots shown + text in the button for expanding/collapsing
   */
  private updateExpandOrCollapseProperties(expanded: boolean) {
    if (expanded) {
      this.displayTradingBotStats = this.filteredTradingBotsStats;
      this.expansionButtonText = "View Less";
    } else {
      this.displayTradingBotStats = [];

      this.displayTradingBotStats = cloneDeep(
        this.filteredTradingBotsStats.slice(
          0,
          this.defaultNumberOfVisibleBotsForScreenSize
        )
      );
      this.expansionButtonText = "View All";
    }
  }
}
